import { useGT } from "gt-react";
import { X } from "lucide-react";
import { useAppLocale } from "../i18n";

interface LegalDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

function LegalDialog({ open, onClose, title, children }: LegalDialogProps) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
    >
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg border border-border bg-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-sm font-semibold text-zinc-200">{title}</h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-zinc-500 hover:bg-surface-raised hover:text-zinc-300"
          >
            <X size={14} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 text-[13px] leading-relaxed text-zinc-400">
          {children}
        </div>
      </div>
    </div>
  );
}

export function ImpressumDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useGT();
  const { locale } = useAppLocale();
  return (
    <LegalDialog open={open} onClose={onClose} title={t("Legal Notice")}>
      {locale !== "de" && (
        <p className="mb-4 rounded border border-warning/30 bg-warning-bg px-3 py-2 text-[12px] text-warning">
          {t("Legal information is currently provided in German only.")}
        </p>
      )}
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        Angaben gemäß § 5 DDG
      </h3>
      <p className="mb-4">
        Sebastian Breitzke<br />
        Stöckener Str. 21<br />
        30419 Hannover<br />
        Deutschland
      </p>

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">Kontakt</h3>
      <p className="mb-4">
        Telefon: +49 151 41928572<br />
        E-Mail: hello@s16e.de
      </p>

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        Verantwortlich für den Inhalt
      </h3>
      <p className="mb-4">
        Sebastian Breitzke<br />
        Stöckener Str. 21<br />
        30419 Hannover
      </p>

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        Haftung für Inhalte
      </h3>
      <p className="mb-4">
        Die Inhalte dieser Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit,
        Vollständigkeit und Aktualität der Inhalte kann ich jedoch keine Gewähr übernehmen.
      </p>

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        Haftung für Links
      </h3>
      <p className="mb-4">
        Diese Website enthält Links zu externen Websites Dritter, auf deren Inhalte ich keinen
        Einfluss habe. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter
        verantwortlich.
      </p>

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        Urheberrecht
      </h3>
      <p>
        Die durch mich erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen
        Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung
        außerhalb der Grenzen des Urheberrechtes bedürfen meiner schriftlichen Zustimmung.
      </p>
    </LegalDialog>
  );
}

export function DatenschutzDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useGT();
  const { locale } = useAppLocale();
  return (
    <LegalDialog open={open} onClose={onClose} title={t("Privacy Policy")}>
      {locale !== "de" && (
        <p className="mb-4 rounded border border-warning/30 bg-warning-bg px-3 py-2 text-[12px] text-warning">
          {t("Legal information is currently provided in German only.")}
        </p>
      )}
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        1. Verantwortlicher
      </h3>
      <p className="mb-4">
        Sebastian Breitzke<br />
        Stöckener Str. 21<br />
        30419 Hannover<br />
        E-Mail: hello@s16e.de
      </p>

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        2. Hosting
      </h3>
      <p className="mb-2">
        Diese Website wird bei Cloudflare Pages gehostet (Cloudflare, Inc., 101 Townsend St,
        San Francisco, CA 94107, USA).
      </p>
      <p className="mb-2">
        Beim Besuch der Website werden automatisch Informationen in Server-Logfiles gespeichert:
        IP-Adresse, Datum und Uhrzeit, angeforderte Seite, HTTP-Statuscode, übertragene Datenmenge,
        Referrer, Browser und Betriebssystem.
      </p>
      <p className="mb-2">
        Cloudflare kann technisch notwendige Cookies setzen (z.B. für Load Balancing und Bot-Schutz).
      </p>
      <p className="mb-2">
        <strong className="text-zinc-300">Datenübertragung in die USA:</strong> Cloudflare ist
        zertifizierter Teilnehmer des EU-US Data Privacy Framework (DPF).
      </p>
      <p className="mb-4">
        Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO (berechtigtes
        Interesse an der technischen Bereitstellung und Sicherheit der Website).
      </p>

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        3. Lokale Datenspeicherung
      </h3>
      <p className="mb-4">
        Spürhund ist eine Local-First-Anwendung. Alle Ihre Daten (Server-Konfigurationen, Queries,
        Schema-Cache, Verlauf) werden ausschließlich lokal in Ihrem Browser gespeichert (IndexedDB).
        Es findet keine serverseitige Speicherung oder Übertragung Ihrer Anwendungsdaten statt.
      </p>

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        4. Kontaktaufnahme
      </h3>
      <p className="mb-4">
        Bei Kontaktaufnahme per E-Mail werden Ihre Angaben zur Bearbeitung der Anfrage gespeichert.
        Rechtsgrundlage: Art. 6 Abs. 1 lit. b bzw. f DSGVO.
      </p>

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        5. Ihre Rechte
      </h3>
      <p className="mb-2">Sie haben das Recht auf:</p>
      <ul className="mb-4 list-inside list-disc space-y-1">
        <li>Auskunft über Ihre gespeicherten Daten (Art. 15 DSGVO)</li>
        <li>Berichtigung unrichtiger Daten (Art. 16 DSGVO)</li>
        <li>Löschung Ihrer Daten (Art. 17 DSGVO)</li>
        <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
        <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
        <li>Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)</li>
      </ul>
      <p className="mb-4">
        Bei Beschwerden können Sie sich an die zuständige Datenschutzaufsichtsbehörde wenden.
      </p>

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        6. Cookies
      </h3>
      <p className="mb-4">
        Diese Website verwendet keine eigenen Cookies. Cloudflare kann technisch notwendige
        Cookies setzen. Es werden keine Tracking-Cookies oder Analyse-Tools eingesetzt.
      </p>

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        7. Änderungen
      </h3>
      <p>
        Diese Datenschutzerklärung kann bei Bedarf angepasst werden. Stand: März 2026.
      </p>
    </LegalDialog>
  );
}
